import React, { useState, useEffect } from "react";
import { Cookie, ChevronDown, ChevronUp, Shield } from "lucide-react";
import useBodyLock from "../utils/useBodyLock";

export const COOKIE_KEY = "dvg_cookie_consent";

function getCookiePreferences() {
    try {
        const stored = localStorage.getItem(COOKIE_KEY);
        if (stored) return JSON.parse(stored);
    } catch { }
    return null;
}

export const COOKIE_CONSENT_EVENT = "dvg_cookie_consent_updated";

function saveCookiePreferences(prefs) {
    try {
        localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...prefs, timestamp: Date.now() }));
        window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));
    } catch { }
}

export function useCookieConsent() {
    const [prefs, setPrefs] = useState(() => getCookiePreferences());

    useEffect(() => {
        const handler = () => setPrefs(getCookiePreferences());
        window.addEventListener(COOKIE_CONSENT_EVENT, handler);
        return () => window.removeEventListener(COOKIE_CONSENT_EVENT, handler);
    }, []);

    return {
        hasConsented: prefs !== null,
        marketingAllowed: prefs?.marketing === true,
        analyticsAllowed: prefs?.analytics === true,
    };
}

export default function CookieConsent() {
    const [visible, setVisible] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const [prefs, setPrefs] = useState({ analytics: true, marketing: true });

    useBodyLock(visible && !leaving);

    useEffect(() => {
        const stored = getCookiePreferences();
        if (!stored) {
            const timer = setTimeout(() => setVisible(true), 600);
            return () => clearTimeout(timer);
        }
    }, []);

    const dismiss = (savedPrefs) => {
        setLeaving(true);
        saveCookiePreferences(savedPrefs ?? prefs);
        setTimeout(() => setVisible(false), 400);
    };

    const acceptAll = () => {
        const all = { analytics: true, marketing: true };
        setPrefs(all);
        dismiss(all);
    };

    const rejectAll = () => {
        const none = { analytics: false, marketing: false };
        setPrefs(none);
        dismiss(none);
    };

    const saveCustom = () => {
        dismiss(prefs);
    };

    if (!visible) return null;

    return (
        <>
            <div
                className={`fixed inset-0 z-[9998] bg-black/20 backdrop-blur-[2px] transition-opacity duration-400 ${leaving ? "opacity-0" : "opacity-100"
                    }`}
                aria-hidden="true"
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-label="Souhlas s cookies"
                className={`fixed z-[9999] bottom-0 left-0 right-0 sm:bottom-4 sm:left-4 sm:right-auto sm:max-w-sm md:max-w-md w-full transition-all duration-400 ${leaving
                        ? "opacity-0 translate-y-4"
                        : "opacity-100 translate-y-0"
                    }`}
            >
                <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                    <div className="h-1 w-full bg-gradient-to-r from-[#050f22] via-[#082359] to-[#050f22]" />

                    <div className="p-5 sm:p-6">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#082359]/10 flex items-center justify-center">
                                <Cookie className="w-5 h-5 text-[#082359]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2 className="text-[15px] font-semibold text-gray-900 leading-snug">
                                    Používáme cookies
                                </h2>
                                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                                    Pomáhají nám zlepšovat naše služby a přizpůsobovat obsah vašim potřebám.
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={() => setExpanded((v) => !v)}
                            className="flex items-center gap-1.5 text-xs text-[#082359] font-medium mb-4 hover:text-[#050f22] transition-colors focus:outline-none focus-visible:underline"
                            aria-expanded={expanded}
                        >
                            {expanded ? (
                                <>
                                    <ChevronUp className="w-3.5 h-3.5" />
                                    Skrýt předvolby
                                </>
                            ) : (
                                <>
                                    <ChevronDown className="w-3.5 h-3.5" />
                                    Přizpůsobit předvolby
                                </>
                            )}
                        </button>

                        {expanded && (
                            <div className="mb-4 space-y-3 rounded-xl bg-gray-50 p-4 border border-gray-100">
                                <CookieToggleRow
                                    label="Nezbytné cookies"
                                    description="Nutné pro fungování webu. Nelze vypnout."
                                    checked={true}
                                    disabled={true}
                                    icon={<Shield className="w-4 h-4 text-gray-400" />}
                                />
                                <div className="border-t border-gray-200" />
                                <CookieToggleRow
                                    label="Analytické cookies"
                                    description="Pomáhají nám porozumět, jak web používáte."
                                    checked={prefs.analytics}
                                    onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                                    icon={<span className="text-sm">📊</span>}
                                />
                                <div className="border-t border-gray-200" />
                                <CookieToggleRow
                                    label="Marketingové cookies"
                                    description="Umožňují personalizovaný obsah a chat podpory."
                                    checked={prefs.marketing}
                                    onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                                    icon={<span className="text-sm">💬</span>}
                                />
                            </div>
                        )}

                        <div className="flex flex-col gap-2">
                            <button
                                onClick={acceptAll}
                                className="w-full py-2.5 rounded-xl bg-[#082359] text-white text-sm font-semibold hover:bg-[#050f22] active:scale-[0.98] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#082359] focus-visible:ring-offset-2"
                            >
                                Přijmout vše
                            </button>

                            <div className="flex gap-2">
                                {expanded ? (
                                    <button
                                        onClick={saveCustom}
                                        className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.98] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                                    >
                                        Uložit výběr
                                    </button>
                                ) : (
                                    <button
                                        onClick={rejectAll}
                                        className="flex-1 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.98] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                                    >
                                        Odmítnout vše
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="px-5 pb-4 sm:px-6">
                        <p className="text-[10px] text-gray-400 leading-relaxed">
                            Přečtěte si naše{" "}
                            <a
                                href="/privacy.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-gray-600 transition-colors"
                            >
                                Zásady ochrany osobních údajů
                            </a>
                            . Vaše preference lze kdykoli změnit.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
}

function CookieToggleRow({ label, description, checked, disabled, onChange, icon }) {
    return (
        <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">{icon}</div>
            <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800">{label}</p>
                <p className="text-[11px] text-gray-500 leading-relaxed">{description}</p>
            </div>
            <button
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => !disabled && onChange?.(!checked)}
                className={`flex-shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#082359] focus-visible:ring-offset-1 ${checked
                        ? disabled
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-[#082359] cursor-pointer"
                        : "bg-gray-200 cursor-pointer"
                    }`}
            >
                <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${checked ? "translate-x-[18px]" : "translate-x-[3px]"
                        }`}
                />
            </button>
        </div>
    );
}
